import { Flex, Text, Icon, Center, Box, Badge } from '_shared/designSystem/components';
import { Link, useMatches, useNavigate } from '@tanstack/react-location';
import PropTypes from 'prop-types';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { backButtonLinkToState } from '_shared/globalState/atoms';
import isEmpty from 'lodash/isEmpty';
import { ClickableHandedness } from '_shared/components/ClickableHandedness';

export const MatchInfoHeader = ({
  subheading,
  matchInfo,
  matchId,
  pointJoinKey,
  onHandednessUpdate,
  disabled = false
}) => {
  let backButtonLinkTo = useRecoilValue(backButtonLinkToState);
  const matches = useMatches();

  const onReviewPage = matches.some((item) => {
    return item.id.includes('review');
  });

  const onMatchPage = matches.some((item) => {
    return item.id.includes('match');
  });

  if (onReviewPage) backButtonLinkTo = '/flagged';
  if (onMatchPage && !backButtonLinkTo.includes('review')) backButtonLinkTo = `/matches`;

  if (isEmpty(matchInfo)) return null;

  const isCollectPage = subheading === 'Collect';

  return (
    <Flex gap={2} justify={isCollectPage ? 'center' : 'flex-start'} w="100%">
      <Flex align="center" justify={isCollectPage ? 'center' : 'flex-start'}>
        <Link to={backButtonLinkTo}>
          <Icon name="arrowLeft" color="primary.500" height={8} width={8} id="back" />
        </Link>
      </Flex>
      <Flex
        align="baseline"
        flexWrap="wrap"
        justify={isCollectPage ? 'center' : 'flex-start'}
        textAlign={isCollectPage ? 'center' : 'left'}
        direction={isCollectPage ? 'column' : 'row'}
      >
        <Box mr={3} display={{ base: 'none', md: 'initial' }} flexShrink={0}>
          <PlayerNames
            subheading={subheading}
            matchInfo={matchInfo}
            isCollectPage={isCollectPage}
            onHandednessUpdate={onHandednessUpdate}
            disabled={disabled}
          />
        </Box>
        <Flex gap={2} flexShrink={1} minW={0} justify={isCollectPage ? 'center' : 'flex-start'}>
          <Text fontSize="md" fontWeight="normal" color="grey.800" textAlign={isCollectPage ? 'center' : 'left'}>
            {matchInfo.competition_details}
          </Text>
          {subheading === 'Review' && <LinkToMatchPage matchId={matchId} pointJoinKey={pointJoinKey} />}
        </Flex>
        <Flex ml={2} flexShrink={0}>
          {matchInfo?.atp_draw_code && <Badge text={matchInfo?.atp_draw_code?.toString()} />}
        </Flex>
      </Flex>
    </Flex>
  );
};

MatchInfoHeader.propTypes = {
  subheading: PropTypes.string,
  matchInfo: PropTypes.object,
  matchId: PropTypes.number,
  pointJoinKey: PropTypes.any,
  onHandednessUpdate: PropTypes.func,
  disabled: PropTypes.bool
};

const LinkToMatchPage = ({ matchId, pointJoinKey }) => {
  const navigate = useNavigate();
  const setbackButton = useSetRecoilState(backButtonLinkToState);

  const openMatchPage = () => {
    setbackButton(`/review/${pointJoinKey}?matchId=${matchId}`);
    navigate({ to: `/match/${matchId}` });
  };

  return (
    <Center cursor="pointer" title="Match Page" data-testid="matchLink" onClick={() => openMatchPage()}>
      <Icon name="tennisBall" color="primary.500" width="13px" height="13px" />
    </Center>
  );
};

const PlayerNames = ({ matchInfo, subheading, isCollectPage, onHandednessUpdate, disabled = false }) => {
  if (!subheading && !matchInfo.player1Name) {
    return (
      <Text fontSize="3xl" fontWeight="medium" color="grey.800">
        {matchInfo.players}
      </Text>
    );
  }

  const openPlayerInformation = (player) => {
    window.open(matchInfo[`${player}ExternalDetailsUrl`], '_blank');
  };

  const showExternalLink = ['Collect', 'Review'].includes(subheading);

  return (
    <Flex
      gap={1}
      flexWrap="wrap"
      align="baseline"
      justify={isCollectPage ? 'center' : 'flex-start'}
      textAlign={isCollectPage ? 'center' : 'left'}
    >
      {subheading && (
        <Text fontSize="3xl" fontWeight="medium" color="grey.800" flexShrink={0}>
          {subheading} -
        </Text>
      )}
      {showExternalLink && (
        <ExternalLinkIcon matchInfo={matchInfo} openPlayerInformation={openPlayerInformation} player="player1" />
      )}
      <Text
        fontSize="3xl"
        fontWeight="medium"
        color="grey.800"
        flexShrink={1}
        minW={0}
        textAlign={isCollectPage ? 'center' : 'left'}
      >
        {matchInfo.player1Name}{' '}
        <ClickableHandedness
          handedness={matchInfo?.player1Handedness}
          playerName={matchInfo.player1Name}
          playerId={matchInfo?.player1_id}
          onHandednessUpdate={onHandednessUpdate}
          disabled={disabled || !onHandednessUpdate}
        />{' '}
        v {matchInfo.player2Name}{' '}
        <ClickableHandedness
          handedness={matchInfo?.player2Handedness}
          playerName={matchInfo.player2Name}
          playerId={matchInfo?.player2_id}
          onHandednessUpdate={onHandednessUpdate}
          disabled={disabled || !onHandednessUpdate}
        />
      </Text>
      {showExternalLink && (
        <ExternalLinkIcon matchInfo={matchInfo} openPlayerInformation={openPlayerInformation} player="player2" />
      )}
    </Flex>
  );
};

const ExternalLinkIcon = ({ matchInfo, player, openPlayerInformation }) => {
  if (!matchInfo[`${player}ExternalDetailsUrl`]) return null;
  return (
    <Center
      data-testid={`externalLink${player}`}
      cursor="pointer"
      title={`${matchInfo[`${player}Name`]} Player Profile`}
      onClick={() => openPlayerInformation(player)}
    >
      <Icon name="externalLink" color="primary.500" width="16px" height="16px" />
    </Center>
  );
};
